import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useControlledField } from '@frontend/design-system';
import { AudioPicker } from '../../audio-picker/audio-picker';
import { playMessageAllowedOptions } from '../../call-routes/call-route-details/add-step-panel/play-message-step';
import { CachedAudioScrubber } from '../../common/cached-audio-scrubber';
import { PlayInstruction } from '../types';
import { ReadOnlyInstructionField } from './read-only-instruction';
import { fieldWidth } from './styles';

export const PLAY_MESSAGE_BREAKPOINT_WIDTH = 760;

export type PlayMessagePayload = { greetingId: string };

export const PlayMessageInstructionFields = ({
  onChange,
  instructionState,
  disabled = false,
  isReadOnly = false,
}: {
  onChange: (payload: PlayInstruction) => void;
  instructionState: PlayInstruction;
  disabled?: boolean;
  isReadOnly?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');

  const greetingIdDropDownProps = useControlledField({
    type: 'dropdown',
    value: instructionState.id,
    onChange: (value) => {
      onChange({ ...instructionState, id: value });
    },
  });

  if (isReadOnly) {
    return (
      <>
        <ReadOnlyInstructionField
          instructionName={instructionState?.name ?? t('Unknown Play Message')}
          iconName={'speaker'}
          instructionType={t('Play Message')}
        />
        <CachedAudioScrubber
          css={{
            alignSelf: 'start',
          }}
          singlePlayer
          mediaId={instructionState?.id || ''}
          filePath={instructionState?.data.filePath || ''}
        />
      </>
    );
  }

  return (
    <AudioPicker
      field={greetingIdDropDownProps}
      name={'greetingId'}
      labels={{ placeholder: t('Play Message') }}
      disabled={disabled}
      enableLoader
      css={css`
        display: flex;
        & > div {
          min-width: 250px;
        }

        @media (max-width: ${PLAY_MESSAGE_BREAKPOINT_WIDTH}px) {
          flex-wrap: wrap;
        }
      `}
      tenantId={settingsTenantLocation?.phoneTenantId ?? ''}
      // Use the same allowed options as in the play message step in the call route details page
      // so that users have a consistent experience. If a user can select a certain option
      // when configuring a play message in a call route, they should be able to select
      // the same option when configuring a play message in the phone tree, or else we won't
      // be able to display the selected audio file name correctly when editing here.
      allowedOptions={playMessageAllowedOptions}
      widths={{ field: fieldWidth, scrubber: instructionState.id ? 200 : 0 }}
    />
  );
};
